import { type ICreateExternalUserPayload } from 'types/payloads/user.payload.types';
import { type ExternalUser } from 'types/user.types';

/**
 *
 * This function is used to handle the change event
 * of the customer account selector.
 * It will add or remove the selected customer account
 * from the internal user's customer accounts array.
 */
export const handleSelectCustomerSingle = (
  event: { target: { value: any } },
  setExternalUser: React.Dispatch<
    React.SetStateAction<ICreateExternalUserPayload>
  >
) => {
  const selectedValues = event.target.value;

  setExternalUser((previousExternalUser) => {
    if (!previousExternalUser) return previousExternalUser;

    // If nothing selected, remove the key entirely
    if (!selectedValues || selectedValues.length === 0) {
      const { userCustomerAssociations, ...restUserData } =
        previousExternalUser;
      return restUserData;
    }

    const lastSelectedValue = selectedValues[selectedValues.length - 1];
    let selectedCustomer: NonNullable<
      ExternalUser['userCustomerAssociations']
    >[number];

    try {
      selectedCustomer = JSON.parse(lastSelectedValue);
    } catch (error) {
      console.error('Failed to parse selected customer:', error);
      return previousExternalUser;
    }

    const previousAssociations =
      previousExternalUser.userCustomerAssociations ?? [];

    const isAlreadySelected = previousAssociations.some(
      (association) => association.id === selectedCustomer.id
    );

    const updatedAssociations = isAlreadySelected
      ? previousAssociations.filter(
          (association) => association.id !== selectedCustomer.id
        )
      : [...previousAssociations, selectedCustomer];

    // if list becomes empty after removal, remove the key entirely
    if (updatedAssociations.length === 0) {
      const { userCustomerAssociations, ...restUserData } =
        previousExternalUser;
      return restUserData;
    }

    return {
      ...previousExternalUser,
      userCustomerAssociations: updatedAssociations,
    };
  });
};

/**
 *
 * This function is used to handle the change event
 * of the customer account selector.
 *
 * It will add or remove all customer accounts
 * from the internal user's customer accounts array.
 *
 */
export const handleSelectCustomerAll = (
  userCustomerAssociationsAll: NonNullable<
    ExternalUser['userCustomerAssociations']
  >,
  setInternalUser: React.Dispatch<
    React.SetStateAction<ICreateExternalUserPayload>
  >
) => {
  setInternalUser((previousInternalUser) => {
    if (!previousInternalUser) return previousInternalUser;

    const currentAssociations =
      previousInternalUser.userCustomerAssociations ?? [];

    const areAllSelected =
      currentAssociations.length === userCustomerAssociationsAll.length &&
      userCustomerAssociationsAll.every((associationAll) =>
        currentAssociations.some((current) => current.id === associationAll.id)
      );

    // If all selected, remove the key entirely
    if (areAllSelected) {
      const { userCustomerAssociations, ...restUserData } =
        previousInternalUser;
      return restUserData;
    }

    // Otherwise, add all
    return {
      ...previousInternalUser,
      userCustomerAssociations: userCustomerAssociationsAll,
    };
  });
};
